import React from 'react';
import { Box, Flex, Group, Skeleton, Truncate, Text } from '@landr/maestro';
import { InstrumentationOptions } from 'types';
import PlayerButton from 'components/CommonUi/PlayerButton/PlayerButton';
import { ButtonComponentProps } from 'components/CommonUi/PlayerButton/types';
import { DemoButton } from 'components/CommonUi/DemoButton';
import { ClassifiersText } from 'components/ClassifiersText/ClassifiersText';
import { SamplesTableNameProps } from './SamplesTableName.types';
import {
    skeletonTitle,
    skeletonTags,
    name as nameStyle,
    playWrapper,
    wrapperStyles,
} from './SamplesTableName.styled';

const NameSkeleton: React.FC = () => {
    return (
        <Box className={nameStyle}>
            <Group direction="vertical" gap="xs">
                <Group.Item>
                    <Skeleton className={skeletonTitle} height="16px" />
                </Group.Item>
                <Group.Item>
                    <Skeleton className={skeletonTags} height="16px" />
                </Group.Item>
            </Group>
        </Box>
    );
};

export const SamplesTableNameSkeleton = () => <NameSkeleton />;

export const SamplesTableName: React.FC<SamplesTableNameProps> = React.memo(
    ({
        sampleGuid,
        packArtworkUrl,
        packName,
        sampleName,
        sampleClassifiers,
        page,
        rowIndex,
        withPlayer,
    }) => {
        const instrumentationOptions: InstrumentationOptions = {
            interactionSource: 'Sample List',
            ...(page && { page: page }),
            ...(rowIndex && { rowIndex: rowIndex }),
        };

        return withPlayer ? (
            <Box width="full" overflow="hidden" marginLeft="none">
                <PlayerButton
                    id={sampleGuid}
                    stopPropagation={true}
                    ButtonComponent={({
                        onClick,
                        playing,
                        isLoading,
                    }: ButtonComponentProps): JSX.Element => (
                        <Box className={playWrapper}>
                            <DemoButton
                                onClick={onClick}
                                playing={playing}
                                isLoading={isLoading}
                                packArtworkUrl={packArtworkUrl}
                                packName={packName}
                                instrumentationOptions={instrumentationOptions}
                                kind="link"
                                variant="ghost"
                                isFull
                            >
                                <Box className={wrapperStyles}>
                                    <Text className={nameStyle}>
                                        {sampleName}
                                    </Text>
                                    <ClassifiersText
                                        classifiers={sampleClassifiers}
                                    />
                                </Box>
                            </DemoButton>
                        </Box>
                    )}
                />
            </Box>
        ) : (
            <Flex flexDirection="column">
                <Text className={nameStyle}>
                    <Truncate mode="middle">{sampleName}</Truncate>
                </Text>
                <ClassifiersText classifiers={sampleClassifiers} />
            </Flex>
        );
    },
    (prevProps, nextProps) => {
        // Compare each prop individually
        return (
            prevProps.sampleGuid === nextProps.sampleGuid &&
            prevProps.packArtworkUrl === nextProps.packArtworkUrl &&
            prevProps.packName === nextProps.packName &&
            prevProps.sampleName === nextProps.sampleName &&
            prevProps.page === nextProps.page &&
            prevProps.rowIndex === nextProps.rowIndex &&
            prevProps.withPlayer === nextProps.withPlayer &&
            // For arrays, we can compare their stringified versions since they don't contain React elements
            JSON.stringify(prevProps.sampleClassifiers) ===
                JSON.stringify(nextProps.sampleClassifiers)
        );
    },
);
